import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { api, asset } from "@/lib/api";
import { useSiteConfig } from "@/context/SiteConfig";
import { JOBS, IMAGES } from "@/data/content";

const JOB_IMG = [IMAGES.town, IMAGES.ridge, IMAGES.townRide];
const TAG_IMG = {
  Law: IMAGES.town, Trade: IMAGES.townCattle, Medical: IMAGES.manifesto,
  Craft: IMAGES.ridge, Service: IMAGES.townRide, Retail: IMAGES.goldRider,
};
const jobImage = (job, i) => (job.image ? asset(job.image) : (TAG_IMG[job.tag] || JOB_IMG[i % JOB_IMG.length]));

export default function Jobs() {
  useTitle("Jobs & Careers | Aurora Springs RP | RedM Roleplay", "Browse jobs and careers in Aurora Springs RP — law, medical, ranch work, saloons, blacksmithing and more on our whitelisted RedM roleplay server.");
  const { img } = useSiteConfig();
  const [jobs, setJobs] = useState(JOBS);
  useEffect(() => {
    api.get("/jobs").then(({ data }) => { if (data?.length) setJobs(data); }).catch(() => {});
  }, []);
  return (
    <>
      <PageHeader overline="Make your living" title="Jobs & Trades" image={img("jobs", IMAGES.town)}
        sub="Whether you're looking to work the land, serve the county, practice medicine, or learn a trade, there are plenty of ways to make a living. Explore the jobs below to see what each has to offer." />
      <section className="mx-auto max-w-7xl px-6 sm:px-10 py-20 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {jobs.map((job, i) => (
          <Reveal key={job.id || job.slug} delay={(i % 6) * 0.05}>
            <Link to={`/jobs/${job.slug}`} data-testid={`job-card-${i}`}
              className="group relative block overflow-hidden border border-[#2A201A] bg-surface/40 h-full hover:border-teal/40 transition-colors">
              <div className="relative h-44 overflow-hidden">
                <img src={jobImage(job, i)} alt={job.title} className="h-full w-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
                {job.tag && <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest text-teal border border-teal/30 bg-obsidian/60 px-2 py-1">{job.tag}</span>}
              </div>
              <div className="p-7">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-2xl font-light">{job.title}</h3>
                  <ArrowUpRight className="h-5 w-5 text-[#8F877C] group-hover:text-teal group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                <p className="mt-3 text-sm text-[#8F877C] leading-relaxed">{job.body}</p>
                {job.pay && <span className="mt-4 inline-block font-mono text-xs text-teal">{job.pay}</span>}
              </div>
            </Link>
          </Reveal>
        ))}
      </section>
    </>
  );
}
